import { isFilterGroup } from '@/types'
import type { StrongModelCriteria, FilterGroup, FilterCondition } from '@/types'

export const DEFAULT_CRITERIA: StrongModelCriteria = {
  time: {
    period_type: 'CUSTOM',
    aggregation_method: 'AVG_WEEKLY',
  },
  product_expression: { op: 'AND', conditions: [] },
  location_expression: { op: 'AND', conditions: [] },
  pog_group: null,
}

const OPERATOR_SYMBOLS: Record<string, string> = {
  eq: '=',
  neq: '!=',
  lt: '<',
  lte: '<=',
  gt: '>',
  gte: '>=',
  like: 'LIKE',
  in: 'IN',
}

function resolveValue(
  raw: string,
  valueLabels?: Record<string, string>
): string {
  const resolved = valueLabels?.[raw] ?? raw
  return `"${resolved}"`
}

function formatValue(
  condition: FilterCondition,
  valueLabels?: Record<string, string>
): string {
  if (condition.operator === 'in') {
    const items = Array.isArray(condition.value)
      ? condition.value
      : String(condition.value)
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
    return `[${items.map(v => resolveValue(v, valueLabels)).join(', ')}]`
  }
  return resolveValue(String(condition.value), valueLabels)
}

function formatCondition(
  condition: FilterCondition,
  fieldLabels: Record<string, string>,
  valueLabels?: Record<string, string>
): string {
  const field = fieldLabels[condition.field] ?? condition.field
  const op = OPERATOR_SYMBOLS[condition.operator] ?? condition.operator
  const value = formatValue(condition, valueLabels)
  return `${field} ${op} ${value}`
}

/**
 * Convert a FilterGroup into a human-readable logic expression string.
 * Optionally resolves value IDs to labels via valueLabels map.
 */
export function filterGroupToExpression(
  group: FilterGroup,
  fieldLabels: Record<string, string>,
  valueLabels?: Record<string, string>
): string {
  if (group.conditions.length === 0) return ''

  const parts = group.conditions
    .map(item => {
      if (isFilterGroup(item)) {
        const nested = filterGroupToExpression(item, fieldLabels, valueLabels)
        return nested ? `(${nested})` : ''
      }
      return formatCondition(item, fieldLabels, valueLabels)
    })
    .filter(Boolean)

  return parts.join(` ${group.op} `)
}

/**
 * Normalize criteria for API submission: convert comma-separated "in" values to arrays.
 */
export function buildProcessedCriteria(
  criteria: StrongModelCriteria
): StrongModelCriteria {
  return {
    time: criteria.time,
    product_expression: criteria.product_expression
      ? processFilterGroup(criteria.product_expression)
      : null,
    location_expression: criteria.location_expression
      ? processFilterGroup(criteria.location_expression)
      : null,
    pog_group: criteria.pog_group,
  }
}

/**
 * Recursively process filter group: convert comma-separated "in" values to arrays
 */
export function processFilterGroup(group: FilterGroup): FilterGroup {
  return {
    ...group,
    conditions: group.conditions.map(item => {
      if (isFilterGroup(item)) {
        return processFilterGroup(item)
      }
      if (item.operator === 'in' && typeof item.value === 'string') {
        return {
          ...item,
          value: item.value
            .split(',')
            .map(v => v.trim())
            .filter(Boolean),
        }
      }
      return item
    }),
  }
}
